import * as React from 'react'
// hooks
import { useSnackbar } from 'notistack'
import { useStateRef } from 'hooks/useStateRef'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IRiskTemplateFormConditionInput, RiskTemplateFormConditionActionType } from 'types/graphql'
// utils
import { omitTypename } from 'utils/graphql'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  currentCondition: IRiskTemplateFormConditionInput | null
  onFieldFilterChange: (fieldId: string, value: any) => void
  setCurrentCondition: (fieldId: string, value: string | boolean, label?: string) => void
  onFieldVisibilityChange: (fieldId: string, value: boolean) => void
  onOptionVisibilityChange: (fieldId: string, optionId: string) => void
}

export const LogicalConditionsContext = React.createContext<IContext>({} as any)

const LogicalConditions: React.FC<Props> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { riskTemplate, updateRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const [currentCondition, setCurrentCondition, currentConditionRef] = useStateRef<any>(null)

  const onDoneClick = () => {
    const currentCondition = currentConditionRef.current
    const hasActions = !!currentCondition.actions?.length
    const isAlreadyExist = riskTemplate.formConditions?.some(
      (item) => item.fieldId === currentCondition.fieldId && item.trigger.value === currentCondition.trigger.value
    )

    if (!hasActions && !isAlreadyExist) {
      setCurrentCondition(null)
      return
    }

    setCurrentCondition(null)

    const preparedConditions =
      isAlreadyExist && !hasActions
        ? riskTemplate.formConditions.filter(
            (condition) =>
              condition.fieldId !== currentCondition.fieldId ||
              (condition.fieldId === currentCondition.fieldId &&
                condition.trigger.value !== currentCondition.trigger.value)
          )
        : isAlreadyExist
          ? riskTemplate.formConditions.map((condition) => {
              if (
                condition.fieldId === currentCondition.fieldId &&
                condition.trigger.value === currentCondition.trigger.value
              ) {
                return currentCondition
              }
              return condition
            })
          : [...riskTemplate.formConditions, currentCondition]

    updateRiskTemplate({ formConditions: JSON.parse(JSON.stringify(preparedConditions, omitTypename)) })
  }

  const handleSetCurrentCondition: IContext['setCurrentCondition'] = (fieldId, value, label) => {
    if (currentCondition) {
      return
    }

    const field = riskTemplate.fields.find((item) => item.id === fieldId)

    enqueueSnackbar(`Set logic for "${field.label}"${label ? ` / "${label}"` : ''}`, {
      variant: 'info',
      action: (key) => getSnackbarCloseButton(key, closeSnackbar, 'DONE', onDoneClick),
      autoHideDuration: null
    })

    const condition = riskTemplate.formConditions.find((c) => c.fieldId === fieldId && c.trigger.value === value)

    setCurrentCondition(condition || { fieldId, trigger: { value } })
  }

  const onFieldFilterChange: IContext['onFieldFilterChange'] = (fieldId, value) => {
    if (currentCondition.fieldId === fieldId) {
      return
    }

    setCurrentCondition((prev) => {
      const prevValue = prev.actions?.find(
        (a) => a.fieldId === fieldId && a.type === RiskTemplateFormConditionActionType.LimitResponse
      )

      if (!value.length) {
        return {
          ...prev,
          actions: prev.actions.filter(
            (a) =>
              a.fieldId !== fieldId ||
              (a.fieldId === fieldId && a.type !== RiskTemplateFormConditionActionType.LimitResponse)
          )
        }
      }

      return {
        ...prev,
        actions: prevValue
          ? prev.actions.map((a) => {
              if (a.fieldId === fieldId && a.type === RiskTemplateFormConditionActionType.LimitResponse) {
                return { ...a, value: { businessUnitsIds: value } }
              }

              return a
            })
          : [
              ...(prev.actions || []),
              {
                type: RiskTemplateFormConditionActionType.LimitResponse,
                fieldId: fieldId,
                value: { businessUnitsIds: value }
              }
            ]
      }
    })
  }

  const onFieldVisibilityChange: IContext['onFieldVisibilityChange'] = (fieldId, value) => {
    if (currentCondition.fieldId === fieldId) {
      return
    }

    setCurrentCondition((prev) => {
      const prevValue = prev.actions?.find(
        (a) => a.fieldId === fieldId && a.type === RiskTemplateFormConditionActionType.Visibility
      )

      if (typeof prevValue?.value === 'boolean' && !prevValue?.value) {
        return {
          ...prev,
          actions: prev.actions.filter(
            (a) =>
              a.fieldId !== fieldId ||
              (a.fieldId === fieldId && a.type !== RiskTemplateFormConditionActionType.Visibility)
          )
        }
      }

      return {
        ...prev,
        actions: prevValue
          ? prev.actions.map((a) => {
              if (a.fieldId === fieldId && a.type === RiskTemplateFormConditionActionType.Visibility) {
                return { ...a, value: value }
              }

              return a
            })
          : [
              ...(prev.actions || []),
              {
                type: RiskTemplateFormConditionActionType.Visibility,
                value: value,
                fieldId: fieldId
              }
            ]
      }
    })
  }

  const onOptionVisibilityChange: IContext['onOptionVisibilityChange'] = (fieldId, optionId) => {
    const field = riskTemplate.fields.find((item) => item.id === fieldId)

    setCurrentCondition((prev) => {
      const action = prev.actions?.find(
        (a) => a.fieldId === field.id && a.type === RiskTemplateFormConditionActionType.LimitOptions
      )

      const isAlreadyAdded = !!action
      const isOptionExist = action?.value.includes(optionId)

      if (isOptionExist) {
        if (action.value.length === 1) {
          return {
            ...prev,
            actions: prev.actions.filter(
              (a) => a.fieldId !== fieldId && a.type !== RiskTemplateFormConditionActionType.LimitOptions
            )
          }
        } else {
          return {
            ...prev,
            actions: prev.actions.map((action) => {
              if (action.fieldId === field.id && action.type === RiskTemplateFormConditionActionType.LimitOptions) {
                return { ...action, value: action.value.filter((id: string) => id !== optionId) }
              }

              return action
            })
          }
        }
      }

      return {
        ...prev,
        actions: isAlreadyAdded
          ? prev.actions.map((action) => {
              if (action.fieldId === field.id && action.type === RiskTemplateFormConditionActionType.LimitOptions) {
                return { ...action, value: [...action.value, optionId] }
              }

              return action
            })
          : [
              ...(prev.actions || []),
              { type: RiskTemplateFormConditionActionType.LimitOptions, fieldId: field.id, value: [optionId] }
            ]
      }
    })
  }

  const value = {
    currentCondition,
    onFieldFilterChange,
    onFieldVisibilityChange,
    onOptionVisibilityChange,
    setCurrentCondition: handleSetCurrentCondition
  }

  return <LogicalConditionsContext.Provider value={value}>{children}</LogicalConditionsContext.Provider>
}

export default LogicalConditions
